import React, { useState, useEffect } from 'react';
import { getAllSupportMessages, addReplyToSupportMessage, updateMessageStatus } from '../services/supportService';
import { formatDistanceToNow } from 'date-fns';

const AdminSupportMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Fetch all support messages
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const supportMessages = await getAllSupportMessages();
        setMessages(supportMessages);
      } catch (err) {
        console.error('Error fetching support messages:', err);
        setError('Failed to load support messages. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
  }, []);
  
  // Filter messages based on status
  const filteredMessages = statusFilter === 'all'
    ? messages
    : messages.filter(msg => msg.status === statusFilter);
  
  // Handle message selection for reply
  const handleSelectMessage = async (message) => {
    setSelectedMessage(message);
    
    // Mark as read if it was unread
    if (message.status === 'unread') {
      try {
        await updateMessageStatus(message.id, 'read');
        
        // Update messages in state
        setMessages(messages.map(msg => 
          msg.id === message.id ? { ...msg, status: 'read' } : msg
        ));
      } catch (err) {
        console.error('Error updating message status:', err);
      }
    }
  };
  
  // Handle sending a reply
  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim() || !selectedMessage) {
      return;
    }
    
    setReplying(true);
    
    try {
      // Send reply to backend
      await addReplyToSupportMessage(selectedMessage.id, replyText);
      
      // Refresh messages to get updated data
      const updatedMessages = await getAllSupportMessages();
      setMessages(updatedMessages);
      
      // Clear reply field
      setReplyText('');
      
      // Update selected message with the new data
      const updatedSelectedMessage = updatedMessages.find(msg => msg.id === selectedMessage.id);
      setSelectedMessage(updatedSelectedMessage);
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply. Please try again.');
    } finally {
      setReplying(false);
    }
  };
  
  // Status badge component
  const StatusBadge = ({ status }) => {
    let bgColor = 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'unread':
        bgColor = 'bg-red-100 text-red-800';
        break;
      case 'read':
        bgColor = 'bg-yellow-100 text-yellow-800';
        break;
      case 'replied':
        bgColor = 'bg-green-100 text-green-800';
        break;
      default:
        break;
    }
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${bgColor}`}>
        {status === 'unread' ? 'New' : status === 'read' ? 'Pending Reply' : 'Replied'}
      </span>
    );
  };
  
  if (loading) {
    return <div className="text-center py-8">Loading support messages...</div>;
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <p>{error}</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Support Messages</h2>
        <p className="text-gray-600 dark:text-gray-400">
          {messages.length} total messages, {messages.filter(m => m.status === 'unread').length} unread
        </p>
        
        <div className="mt-4">
          <label htmlFor="statusFilter" className="mr-2 text-gray-700 dark:text-gray-300">
            Filter by status:
          </label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Messages</option>
            <option value="unread">New Messages</option>
            <option value="read">Pending Reply</option>
            <option value="replied">Replied</option>
          </select>
        </div>
      </div>
      
      <div className="flex">
        {/* Message List */}
        <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 overflow-y-auto" style={{ maxHeight: '600px' }}>
          {filteredMessages.length === 0 ? (
            <p className="p-4 text-gray-600 dark:text-gray-400">No messages found.</p>
          ) : (
            <ul>
              {filteredMessages.map(message => (
                <li 
                  key={message.id}
                  className={`p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 ${
                    selectedMessage?.id === message.id ? 'bg-blue-50 dark:bg-blue-900' : ''
                  }`}
                  onClick={() => handleSelectMessage(message)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{message.subject}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        From: {message.userName || message.userEmail}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {formatDistanceToNow(message.createdAt)} ago
                      </p>
                    </div>
                    <StatusBadge status={message.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        
        {/* Message Detail View */}
        <div className="w-2/3 p-4 overflow-y-auto" style={{ maxHeight: '600px' }}>
          {selectedMessage ? (
            <div>
              <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-start">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedMessage.subject}</h2>
                  <StatusBadge status={selectedMessage.status} />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  From: {selectedMessage.userName} ({selectedMessage.userEmail})
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Sent {formatDistanceToNow(selectedMessage.createdAt)} ago
                </p>
              </div>
              
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">Message:</h3>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap p-3 bg-gray-50 dark:bg-gray-750 rounded">
                  {selectedMessage.message}
                </p>
              </div>
              
              {/* Reply History */}
              {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">Previous Replies:</h3>
                  <div className="space-y-3">
                    {selectedMessage.replies.map((reply, index) => (
                      <div key={index} className="bg-blue-50 dark:bg-blue-900 p-3 rounded">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{reply.text}</p>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Reply from {reply.adminName} {reply.createdAt ? `- ${formatDistanceToNow(reply.createdAt.toDate())} ago` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Reply Form */}
              <div className="mt-4">
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">Reply:</h3>
                <form onSubmit={handleSendReply}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows="4"
                    placeholder="Type your reply here..."
                    required
                  ></textarea>
                  
                  <button
                    type="submit"
                    disabled={replying || !replyText.trim()}
                    className={`mt-2 py-2 px-4 rounded-md text-white font-medium ${
                      replying || !replyText.trim()
                        ? 'bg-blue-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {replying ? 'Sending...' : 'Send Reply'}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-600 dark:text-gray-400">Select a message to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportMessages; 